import type { ApiRequests } from '@/utils/requests/api-requests'

import { getNativeVerificationErrorMessage } from './helpers'
import type {
    NativePaymentOrder,
    NativePaymentPlatform,
    NativePaymentSku,
    NativePaymentSuccessPayload,
    NativePurchaseTransaction,
    NativePurchaseVerifyResponse,
} from './types'

type VerifyNativePurchaseParams = {
    api: ApiRequests
    clid: string | undefined
    order: NativePaymentOrder
    packAmount: number
    platform: NativePaymentPlatform
    transaction: NativePurchaseTransaction
}

export async function fetchNativePaymentSkus(api: ApiRequests, platform: NativePaymentPlatform, bundleId: string) {
    const { originalResponse, data, errors } = await api.request<NativePaymentSku[]>(
        `/api/v1/in-game-payments/native-payment/skus?${new URLSearchParams({ platform, bundleId })}`,
    )

    if (!originalResponse.ok || !data) {
        throw new Error(errors[0] || 'Failed to load native payment packs')
    }

    return data.filter((sku) => sku.platform === platform && sku.bundleId === bundleId)
}

export async function verifyNativePurchase({
    api,
    clid,
    order,
    packAmount,
    platform,
    transaction,
}: VerifyNativePurchaseParams): Promise<NativePaymentSuccessPayload> {
    if (!transaction.transactionId || !transaction.receipt) {
        throw new Error('Native purchase verification failed: transaction data is incomplete')
    }

    const verificationTransaction =
        platform === 'ios'
            ? {
                  transactionId: transaction.transactionId,
                  receipt: transaction.receipt,
              }
            : {
                  transactionId: transaction.transactionId,
                  purchaseToken: transaction.receipt,
              }

    const { originalResponse, data, errors, bodyText } = await api.request<NativePurchaseVerifyResponse>(
        '/api/v1/in-game-payments/native-payment/verify',
        {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                platform,
                bundleId: transaction.bundleId,
                clid,
                sku: transaction.sku,
                transaction: verificationTransaction,
                order: getNativePaymentOrderForVerification(order),
            }),
        },
    )

    if (!originalResponse.ok || data?.status !== 'success') {
        throw new Error(getNativeVerificationErrorMessage(originalResponse, bodyText, errors))
    }

    return {
        transactionId: data.transactionId,
        amount: packAmount,
    }
}

function getNativePaymentOrderForVerification(order: NativePaymentOrder) {
    if (!order || order.type === 'topup') {
        return null
    }

    // gamAmount is the wallet-side view only, as in the signed purchase body
    return { ...order, gamAmount: undefined }
}
